import { useCallback } from "react";
import type { ActorRefFrom, SnapshotFrom } from "xstate";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import type { artworkMachine } from "@design-lab/entities/artwork/model/artwork.machine";
import { ArtworkFeedbackDesktop } from "./ArtworkFeedbackDesktop";
import { ArtworkFeedbackMobile } from "./ArtworkFeedbackMobile";

export type FeedbackStates = {
  isIdleState: boolean;
  isFeedbackFormState: boolean;
  isThankYouState: boolean;
  isThankYouNegativeState: boolean;
};

type ArtworkFeedbackProps = {
  artworkState: SnapshotFrom<typeof artworkMachine>;
  sendArtwork: ActorRefFrom<typeof artworkMachine>["send"];
};

export function ArtworkFeedback({ artworkState, sendArtwork }: ArtworkFeedbackProps) {
  const { isDesktop } = useResponsive();

  const onPositiveClick = useCallback(() => {
    sendArtwork({ type: "feedback.thumbs_up" });
  }, [sendArtwork]);

  const onNegativeClick = useCallback(() => {
    sendArtwork({ type: "feedback.thumbs_down" });
  }, [sendArtwork]);

  const onClose = useCallback(() => {
    sendArtwork({ type: "feedback.close" });
  }, [sendArtwork]);

  const feedbackStates = {
    isIdleState: artworkState.matches({ finished: { feedback: "idle" } }),
    isFeedbackFormState: artworkState.matches({ finished: { feedback: "feedbackForm" } }),
    isThankYouState: artworkState.matches({ finished: { feedback: "thankYou" } }),
    isThankYouNegativeState: artworkState.matches({
      finished: { feedback: "thankYouNegative" }
    })
  };

  const isInFeedbackState =
    feedbackStates.isIdleState ||
    feedbackStates.isFeedbackFormState ||
    feedbackStates.isThankYouState ||
    feedbackStates.isThankYouNegativeState;

  if (!isInFeedbackState) {
    return null;
  }

  return isDesktop ? (
    <ArtworkFeedbackDesktop
      artworkState={artworkState}
      sendArtwork={sendArtwork}
      onPositiveClick={onPositiveClick}
      onNegativeClick={onNegativeClick}
      onClose={onClose}
      feedbackStates={feedbackStates}
    />
  ) : (
    <ArtworkFeedbackMobile
      artworkState={artworkState}
      sendArtwork={sendArtwork}
      onPositiveClick={onPositiveClick}
      onNegativeClick={onNegativeClick}
      onClose={onClose}
      feedbackStates={feedbackStates}
    />
  );
}
